import React, { useEffect, useState, useContext } from "react";
import ReactApexChart from "react-apexcharts";
import ChartModal from "./ChartModal"; // Assuming this modal handles chart type selection
import AppContext from "../../../../context/AppContext";
import { fetchUserCount } from "../../../../service/UserDashboard";
import translations from "../../../common/Translations";
import ActionIconButton from "../../../common/ActionIconButton";
import { BarChart3, Eye, UserRound } from "lucide-react";

const UserCountChart = ({ selectedOrgId, loading, setLoading }) => {
  const [userCount, setUserCount] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState("");
  const { setAdminCount, setFieldAgentCount, setOfficeUser, langMode, theme } =
    useContext(AppContext);
  const [chartView, setChartView] = useState("chart");
  const seriesData = Object.values(userCount);

  const defaultColors = ["#673ab7", "#00897b", "#ffb300"]; // Deeper shades for default
  const darkThemeColors = ["#9c27b0", "#26a69a", "#ffca28"]; // Lighter shades for dark theme
  const highContrastColors = ["#7b1fa2", "#00695c", "#ff8f00"]; // Stronger contrast

  const getChartColors = () => {
    if (theme === "dark") return darkThemeColors;
    if (theme === "high-contrast") return highContrastColors;
    return defaultColors;
  };

  const formatLabel = (label) => {
    switch (label) {
      case "fieldAgentCount":
        return translations[langMode].field_agent;
      case "officeUserCount":
        return translations[langMode].office_user;
      case "adminCount":
        return translations[langMode].admin;
      default:
        return label;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await fetchUserCount(selectedOrgId);

        if (data.header?.code === 601) {
          setError(translations[langMode].selectOrganizationPrompt || "Please select an organization.");
          setUserCount({}); // Clear previous data
          setOfficeUser(0);
          setFieldAgentCount(0);
          setAdminCount(0);
        } else if (data.body && data.body.value) {
          const value = data.body.value.summary || data.body.value;
          const fetchedUserCounts = {
            adminCount: Number(value.adminCount) || 0,
            officeUserCount: Number(value.officeUserCount) || 0,
            fieldAgentCount: Number(value.fieldAgentCount) || 0,
          };
          setUserCount(fetchedUserCounts);
          setOfficeUser(fetchedUserCounts.officeUserCount);
          setFieldAgentCount(fetchedUserCounts.fieldAgentCount);
          setAdminCount(fetchedUserCounts.adminCount);
          setError(""); // Clear any previous errors
        } else {
          setError(translations[langMode].UnexpectedResponseFormat || "Unexpected response format.");
          setUserCount({}); // Clear previous data
          setOfficeUser(0);
          setFieldAgentCount(0);
          setAdminCount(0);
        }
      } catch (err) {
        setError(translations[langMode].failedToFetchUserCount || "Failed to fetch user count. Please try again.");
        setUserCount({}); // Clear previous data
        setOfficeUser(0);
        setFieldAgentCount(0);
        setAdminCount(0);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    if (selectedOrgId) { // Only fetch if an organization is selected
      fetchData();
    } else {
      setError(translations[langMode].selectOrganizationPrompt || "Please select an organization.");
      setUserCount({});
      setOfficeUser(0);
      setFieldAgentCount(0);
      setAdminCount(0);
      setLoading(false);
    }
  }, [selectedOrgId, langMode, setLoading, setAdminCount, setFieldAgentCount, setOfficeUser, translations]);

  const maxUserCount = Math.max(...Object.values(userCount));
  const normalizedSeriesData = Object.values(userCount).map(count =>
    maxUserCount > 0 ? (count / maxUserCount) * 100 : 0
  );

  const categories = Object.keys(userCount).map(formatLabel);

  const baseChartOptions = {
    chart: {
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: {
          enabled: true,
          delay: 150
        },
        dynamicAnimation: {
          enabled: true,
          speed: 350
        }
      },
      foreColor: theme === "dark" ? "#E2E8F0" : theme === "high-contrast" ? "#FFF" : "#333", // Global text color
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: "12px",
        colors: [theme === "high-contrast" ? "#000" : (theme === "dark" ? "#FFF" : "#fff")], // Data label color
      },
    },
    legend: {
      show: true, // Keep legend for clarity, though not prominent in the image
      position: "bottom",
      fontSize: "13px",
      labels: {
        colors: theme === "dark" ? "#E2E8F0" : theme === "high-contrast" ? "#FFF" : "#333", // Legend label color
      },
      itemMargin: {
        horizontal: 10,
        vertical: 5
      }
    },
    tooltip: {
      enabled: true,
      theme: theme === "dark" ? "dark" : "light",
      style: {
        fontSize: "13px",
        fontFamily: "inherit",
        background: theme === "dark" ? "#333" : theme === "high-contrast" ? "#FFF" : "#f4f4f4",
        color: theme === "dark" ? "#fff" : theme === "high-contrast" ? "#000" : "#fff",
      },
      y: {
        formatter: function (val, { seriesIndex, dataPointIndex, w }) {
          const originalValue = Object.values(userCount)[seriesIndex];
          return originalValue + " users";
        }
      }
    },
    colors: getChartColors(), // Dynamic colors
  };

  const RadialBarChartView = ({ series, labels }) => {
    const totalUserCount = Object.values(userCount).reduce((a, b) => a + b, 0);

    const radialBarOptions = {
      ...baseChartOptions,
      chart: {
        ...baseChartOptions.chart,
        type: "radialBar",
      },
      plotOptions: {
        radialBar: {
          offsetY: 0,
          startAngle: 0,
          endAngle: 270,
          hollow: {
            margin: 5,
            size: '30%', // Adjust size to create concentric effect
            background: 'transparent',
            image: undefined,
          },
          track: {
            show: true,
            startAngle: undefined,
            endAngle: undefined,
            background: theme === "dark" ? "#374151" : "#e0e0e0", // Track color for un-filled part
            strokeWidth: '97%', // Make track thinner to create gap
            opacity: 1,
            margin: 10, // Margin between each ring
            dropShadow: {
              enabled: true,
              top: 2,
              left: 0,
              blur: 4,
              opacity: 0.15
            }
          }
          ,
          dataLabels: {
            show: true,
            name: {
              show: true,
              fontSize: '16px',
              fontFamily: 'MediumFont, Manrope, sans-serif',
              fontWeight: 600,
              color: baseChartOptions.chart.foreColor,
              offsetY: -10
            },
            value: {
              show: true,
              fontSize: '14px',
              fontFamily: 'MediumFont, Manrope, sans-serif',
              fontWeight: 700,
              color: baseChartOptions.chart.foreColor,
              offsetY: 5,
              formatter: function (val) {
                return (translations[langMode].usersCountLabel || "{n} users").replace("{n}", Math.round(val));
              }

            },
            total: {
              show: true,
              label: translations[langMode].totalUsers || "Total Users",
              color: baseChartOptions.chart.foreColor,
              formatter: function (w) {
                return totalUserCount;
              },
            },
          }
        }
      },
      fill: {
        type: 'gradient',
        gradient: {
          shade: 'dark',
          type: 'horizontal',
          shadeIntensity: 0.5,
          gradientToColors: getChartColors(), // Ensure gradients use the defined colors
          inverseColors: true,
          opacityFrom: 1,
          opacityTo: 1,
          stops: [0, 100]
        }
      },
      labels: labels, // Labels for each series (outer ring)
      stroke: {
        lineCap: 'round' // Rounded ends for the bars
      },
      legend: {
        show: true, // Keep legend for clarity
        position: 'bottom',
        horizontalAlign: 'center',
        offsetY: 0,
        itemMargin: {
          horizontal: 6,
          vertical: 2,
        },
        markers: {
          fillColors: getChartColors()
        }
      },
      responsive: [
        {
          breakpoint: 640,
          options: {
            plotOptions: {
              radialBar: {
                offsetY: -14,
                hollow: {
                  size: '26%',
                },
                track: {
                  margin: 6,
                },
                dataLabels: {
                  name: {
                    fontSize: '13px',
                    offsetY: -6,
                  },
                  value: {
                    fontSize: '12px',
                    offsetY: 4,
                  },
                  total: {
                    fontSize: '12px',
                  },
                },
              },
            },
            legend: {
              position: 'bottom',
              horizontalAlign: 'center',
              fontSize: '11px',
              offsetY: -6,
              itemMargin: {
                horizontal: 4,
                vertical: 2,
              },
            },
          },
        },
        {
          breakpoint: 380,
          options: {
            plotOptions: {
              radialBar: {
                offsetY: -18,
                hollow: {
                  size: '22%',
                },
                track: {
                  margin: 5,
                },
                dataLabels: {
                  name: {
                    fontSize: '11px',
                    offsetY: -4,
                  },
                  value: {
                    fontSize: '11px',
                    offsetY: 2,
                  },
                  total: {
                    fontSize: '11px',
                  },
                },
              },
            },
            legend: {
              position: 'bottom',
              horizontalAlign: 'center',
              fontSize: '10px',
              offsetY: -6,
              itemMargin: {
                horizontal: 3,
                vertical: 1,
              },
            },
          },
        },
      ],
    };

    return (
      <div id="user-radial-bar-chart" className="w-full h-full min-w-0 flex justify-center items-center">
        <ReactApexChart
          options={radialBarOptions}
          series={seriesData} // Use normalized data for percentages
          type="radialBar"
          width="100%"
          height="100%"
        />
      </div>
    );
  };
  const DonutChartView = ({ series, labels }) => {
    const donutOptions = {
      ...baseChartOptions,
      labels: labels,
      dataLabels: {
        ...baseChartOptions.dataLabels,
        formatter: function (val, opts) {
          const total = opts.w.globals.seriesTotals.reduce((a, b) => a + b, 0);
          return total > 0 ? `${val.toFixed(1)}%` : '';
        }
      },
      plotOptions: {
        pie: {
          donut: {
            size: '65%',
            labels: {
              show: true,
              name: {
                show: true,
                fontSize: "14px",
                color: baseChartOptions.chart.foreColor,
              },
              value: {
                show: true,
                fontSize: "18px",
                fontWeight: 600,
                color: baseChartOptions.chart.foreColor,
                formatter: function (val) {
                  return val;
                },
              },
              total: {
                show: true,
                showAlways: true,
                label: translations[langMode].totalUsers || "Total Users",
                fontSize: '14px',
                fontWeight: 600,
                color: baseChartOptions.chart.foreColor,
                formatter: function (w) {
                  return w.globals.seriesTotals.reduce((a, b) => {
                    return a + b;
                  }, 0);
                }
              },
            },
          },
        },
      },
      stroke: {
        show: true,
        colors: [theme === "dark" ? "#1F2937" : (theme === "high-contrast" ? "#000" : "#fff")],
        width: 2
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val + " users";
          }
        }
      }
    };

    return (
      <div id="user-donut-chart" className="w-full h-full flex justify-center items-center">
        <ReactApexChart
          options={donutOptions}
          series={series}
          type="donut"
          width="100%"
          height="100%"
        />
      </div>
    );
  };

  const BarChartView = ({ series, categories }) => {
    const barOptions = {
      ...baseChartOptions,
      chart: {
        ...baseChartOptions.chart,
        type: "bar",
        background: theme === "dark" ? "#1F2937" : "#FFFFFF",
      },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            colors: baseChartOptions.chart.foreColor,
            fontSize: '13px', // Slightly larger font size for labels
            fontWeight: 500,
          },
        },
        axisBorder: {
          show: true,
          color: theme === "dark" ? "#4A5568" : "#E2E8F0",
        },
        axisTicks: {
          show: true,
          color: theme === "dark" ? "#4A5568" : "#E2E8F0",
        },
      },
      yaxis: {
        labels: {
          style: {
            colors: baseChartOptions.chart.foreColor,
            fontSize: '13px', // Slightly larger font size for labels
            fontWeight: 500,
          },
          formatter: function (val) {
            return Math.floor(val); // Ensure whole numbers for user counts
          }
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        title: {
          text: translations[langMode].numberOfUsers || "Number of Users",
          style: {
            color: baseChartOptions.chart.foreColor,
            fontSize: '14px',
            fontWeight: 600,
          }
        }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 8, // More rounded corners for a softer look
          columnWidth: '45%', // Slightly thinner columns
          distributed: true, // Distribute colors across categories
          dataLabels: {
            position: 'top',
            offsetY: -20, // Adjust offset to prevent overlap
          },
        },
      },
      dataLabels: {
        enabled: true,
        offsetY: -20, // Position data labels above the bars
        style: {
          fontSize: '13px',
          colors: [theme === "dark" ? "#FFF" : "#333"], // Ensure good contrast
          fontWeight: 600,
        },
        background: {
          enabled: true,
          foreColor: theme === "dark" ? "#1F2937" : "#FFFFFF",
          padding: 6,
          borderRadius: 4,
          borderWidth: 1,
          borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
          opacity: 0.9,
          dropShadow: {
            enabled: true,
            top: 1,
            left: 1,
            blur: 2,
            opacity: 0.2,
          }
        },
        formatter: function (val) {
          return val; // Display the raw count
        }
      },
      grid: {
        show: true,
        borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
        strokeDashArray: 4, // Dashed grid lines
        position: 'back',
        xaxis: {
          lines: {
            show: false // No vertical grid lines for cleaner look
          }
        },
        yaxis: {
          lines: {
            show: true // Keep horizontal grid lines for readability
          }
        },
        padding: {
          left: 0,
          right: 0
        }
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: function (val) {
            return val + " users";
          }
        },
        x: {
          formatter: function (val) {
            return val;
          }
        },
      },
      colors: getChartColors(), // Apply dynamic colors
      legend: {
        show: false // Hide legend for single series bar chart
      },
      // Adding responsive options for better scaling
      responsive: [{
        breakpoint: 768, // Adjust for medium screens and smaller
        options: {
          plotOptions: {
            bar: {
              columnWidth: '60%',
            }
          },
          dataLabels: {
            offsetY: -15,
            style: {
              fontSize: '11px',
            }
          },
          xaxis: {
            labels: {
              style: {
                fontSize: '11px',
              }
            }
          },
          yaxis: {
            labels: {
              style: {
                fontSize: '11px',
              }
            },
            title: {
              style: {
                fontSize: '12px',
              }
            }
          }
        }
      },
      {
        breakpoint: 480, // Adjust for small screens
        options: {
          plotOptions: {
            bar: {
              columnWidth: '70%',
            }
          },
          dataLabels: {
            offsetY: -10,
            style: {
              fontSize: '10px',
            }
          },
          xaxis: {
            labels: {
              style: {
                fontSize: '10px',
              }
            }
          },
          yaxis: {
            labels: {
              style: {
                fontSize: '10px',
              }
            },
            title: {
              style: {
                fontSize: '11px',
              }
            }
          }
        }
      }
      ]
    };

    return (
      <div id="user-bar-chart" className="w-full h-full "> {/* Added padding */}
        <ReactApexChart
          options={barOptions}
          series={[{ name: translations[langMode].userCount || "User Count", data: series }]}
          type="bar"
          width="100%"
          height="100%"
        />
      </div>
    );
  };

  // --- Area Chart View ---
  const AreaChartView = ({ series, categories }) => {
    const areaOptions = {
      ...baseChartOptions,
      chart: {
        ...baseChartOptions.chart,
        type: "area",
      },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            colors: baseChartOptions.chart.foreColor,
          },
        },
        tooltip: {
          enabled: false
        }
      },
      yaxis: {
        labels: {
          style: {
            colors: baseChartOptions.chart.foreColor,
          },
        },
      },
      stroke: {
        curve: "smooth",
        width: 3,
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          stops: [0, 90, 100]
        }
      },
      dataLabels: {
        enabled: true,
        background: {
          enabled: true,
          foreColor: theme === "high-contrast" ? "#FFF" : (theme === "dark" ? "#1F2937" : "#FFF"),
          padding: 4,
          borderRadius: 2,
          borderWidth: 1,
          borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
          opacity: 0.9,
        },
      },
      grid: {
        show: true,
        borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
        strokeDashArray: 4,
        position: 'back'
      },
      legend: { // Area charts generally have legends for multiple series, but this is single series
        show: false
      }
    };

    return (
      <div id="user-area-chart" className="w-full h-full ">
        <ReactApexChart
          options={areaOptions}
          series={[{ name: translations[langMode].userCount || "User Count", data: series }]}
          type="area"
          width="100%"
          height="100%"
        />
      </div>
    );
  };

  const RadarChartView = ({ series, categories }) => {
    const radarOptions = {
      ...baseChartOptions,
      chart: {
        ...baseChartOptions.chart,
        type: "radar",
      },
      xaxis: {
        categories: categories,
        labels: {
          style: {
            colors: baseChartOptions.chart.foreColor,
          },
        },
      },
      yaxis: {
        show: false,
      },
      stroke: {
        show: true,
        width: 2,
        curve: 'smooth',
        lineCap: 'round',
      },
      fill: {
        opacity: 0.4,
      },
      markers: {
        size: 5,
        hover: {
          size: 7,
        },
        colors: getChartColors(),
        strokeColors: theme === "dark" ? "#1F2937" : (theme === "high-contrast" ? "#000" : "#fff"),
        strokeWidth: 2,
      },
      dataLabels: {
        enabled: true,
        style: {
          fontSize: '12px',
          colors: [baseChartOptions.chart.foreColor]
        },
        background: {
          enabled: true,
          foreColor: theme === "high-contrast" ? "#FFF" : (theme === "dark" ? "#1F2937" : "#FFF"),
          padding: 4,
          borderRadius: 2,
          borderWidth: 1,
          borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
          opacity: 0.9,
        },
      },
      grid: {
        show: true,
        borderColor: theme === "dark" ? "#4A5568" : "#E2E8F0",
        strokeDashArray: 4,
        position: 'back',
        xaxis: {
          lines: {
            show: true
          }
        },
        yaxis: {
          lines: {
            show: true
          }
        },
        padding: {
          left: 20,
          right: 20
        }
      },
      legend: { // Radar charts usually have legends
        show: true
      }
    };

    return (
      <div id="user-radar-chart" className="w-full h-full">
        <ReactApexChart
          options={radarOptions}
          series={[{ name: translations[langMode].userCount || "User Count", data: series }]}
          type="radar"
          width="100%"
          height="100%"
        />
      </div>
    );
  };
  const isDataEmpty = seriesData.every(val => val === 0) && categories.length > 0;

  return (
    <div className={`h-full w-full flex flex-col ${theme === "dark" ? "text-gray-200" : "text-gray-900"}`}>
      <div className="flex items-center justify-between px-1 sm:px-2 py-1 sm:py-2 w-full gap-2">
        <h2 className="flex items-center gap-1.5 sm:gap-2 text-left text-xs sm:text-base font-semibold min-w-0 flex-1">
          <UserRound className="text-blue-500 shrink-0" size={18} />
          <span className="truncate">{translations[langMode].usercount}</span>
        </h2>

        <div className="shrink-0">
          <ActionIconButton
            type="button"
            tone="gray"
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            onClick={() => setShowModal(true)}
            aria-label={translations[langMode].ViewChart}
            tooltipText={translations[langMode].ViewChart}
            tooltipPosition="bottom"
          >
            <Eye size={18} />
          </ActionIconButton>
        </div>

      </div>

      {(error || isDataEmpty) ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center px-4">
          <span
            className={`flex h-12 w-12 items-center justify-center rounded-full
              ${theme === "dark"
                ? "bg-[#236DB4]/20 text-[#236DB4]"
                : theme === "high-contrast"
                  ? "bg-black text-yellow-300"
                  : "bg-[#236DB4]/10 text-[#236DB4]"
              }`}
          >
            <BarChart3 className="h-6 w-6" />
          </span>
          <div className="flex flex-col gap-1">
            <p className={`text-sm font-medium ${theme === "high-contrast" ? "text-yellow-300" : theme === "dark" ? "text-gray-100" : "text-gray-900"}`}>
              {translations[langMode].noDataAvailable || translations[langMode].notchartYet}
            </p>
            <p className={`text-sm ${theme === "high-contrast" ? "text-yellow-200" : "text-gray-500"}`}>
              {translations[langMode].chartWaitingForResponses}
            </p>
          </div>
        </div>
      ) : (
        <div className="mt-1 flex-1 min-h-0 w-full">
          {chartView === "chart" ? (
            <RadialBarChartView series={normalizedSeriesData} labels={categories} />
          ) : chartView === "donut" ? (
            <DonutChartView series={seriesData} labels={categories} />
          ) : chartView === "area" ? (
            <AreaChartView series={seriesData} categories={categories} />
          ) : chartView === "radar" ? (
            <RadarChartView series={seriesData} categories={categories} />
          ) : (
            <BarChartView series={seriesData} categories={categories} />
          )}
        </div>
      )}

      <ChartModal
        showModal={showModal}
        setShowModal={setShowModal}
        setChartView={setChartView}
        currentChartView={chartView}
        availableChartTypes={["radialBar", "bar", "area", "radar"]}
      />
    </div>
  );
};

export default UserCountChart;
